
import { useMemo, useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories, useMainCategories } from "@/hooks/use-categories";
import { FolderOpen, Loader2 } from "lucide-react";

interface CategorySelectProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function CategorySelect({ value, onChange, placeholder = "Select a category" }: CategorySelectProps) {
  const { data: allCategories, isLoading: isLoadingAll } = useCategories();
  const { data: mainCategories, isLoading: isLoadingMain } = useMainCategories();
  
  const [selectedMainCategory, setSelectedMainCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  
  // Filter subcategories based on selected main category
  const subcategories = useMemo(() => {
    if (!selectedMainCategory || !allCategories) return [];
    return allCategories.filter(cat => cat.parent_id === selectedMainCategory);
  }, [selectedMainCategory, allCategories]);
  
  // Format category name for display
  const formatCategoryName = (id: string | null) => {
    if (!id || !allCategories) return "";
    const category = allCategories.find(c => c.id === id);
    return category?.name || "";
  };

  // Determine the full category path for display
  const displayValue = useMemo(() => {
    if (!value || !allCategories) return placeholder;
    
    const category = allCategories.find(c => c.id === value);
    if (!category) return placeholder;
    
    if (!category.parent_id) {
      // It's a main category
      return category.name;
    }
    
    const parent = allCategories.find(c => c.id === category.parent_id);
    if (!parent) return category.name;
    
    // It's a subcategory
    return `${parent.name} › ${category.name}`;
  }, [value, allCategories, placeholder]);

  // Initialize selected categories based on the value
  useEffect(() => {
    if (value && allCategories) {
      const selectedCategory = allCategories.find(c => c.id === value);
      if (selectedCategory) {
        if (selectedCategory.parent_id) {
          // This is a subcategory
          setSelectedMainCategory(selectedCategory.parent_id);
          setSelectedSubCategory(selectedCategory.id);
        } else {
          // This is a main category
          setSelectedMainCategory(selectedCategory.id);
          setSelectedSubCategory(null);
        }
      }
    }
  }, [value, allCategories]);
  
  const handleMainCategoryChange = (mainCatId: string) => {
    setSelectedMainCategory(mainCatId);
    setSelectedSubCategory(null);
    onChange(mainCatId); // Set the main category as the current value
  };
  
  const handleSubCategoryChange = (subCatId: string) => {
    setSelectedSubCategory(subCatId);
    onChange(subCatId); // Set the subcategory as the current value
  };

  const isLoading = isLoadingAll || isLoadingMain;
  
  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 h-10 px-3 py-2 border border-input rounded-md">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Display the current selection */}
      <div className="text-sm flex items-center text-muted-foreground">
        <FolderOpen className="h-4 w-4 mr-2" />
        <span>{displayValue}</span>
      </div>
      
      {/* Main Category Selection */}
      <div>
        <label className="text-sm font-medium">Main Category</label>
        <Select value={selectedMainCategory || ""} onValueChange={handleMainCategoryChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select main category" />
          </SelectTrigger>
          <SelectContent>
            {mainCategories?.map((mainCat) => (
              <SelectItem key={mainCat.id} value={mainCat.id}>
                {mainCat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Subcategory Selection - only show if main category is selected */}
      {selectedMainCategory && subcategories.length > 0 && (
        <div>
          <label className="text-sm font-medium">Subcategory</label>
          <Select value={selectedSubCategory || ""} onValueChange={handleSubCategoryChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select subcategory" />
            </SelectTrigger>
            <SelectContent>
              {subcategories.map((subCat) => (
                <SelectItem key={subCat.id} value={subCat.id}>
                  {subCat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
